import t from 'react-translate';
import {
  ReactComponent as InsightGrow,
} from 'styles/icons/insight-grow.svg';
import {
  ReactComponent as Like,
} from 'styles/icons/went-well-like.svg';
import { css } from '@emotion/react';
import {
  gray5,
  hexToRgbaString,
  lowTide,
  success,
  warning,
} from 'styles/global_defaults/colors';
import { dunes, seafoam } from 'athena/styles/colors';
import prodPathReplace from 'shared/prod-path-rewrite';
import NvGradientBorderButton from 'shared/components/button/nv-gradient-border-button';
import NvIcon from 'shared/components/nv-icon';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import { useContext, useState } from 'react';
import NvConfetti from 'shared/components/nv-confetti';
import DotsFlowAnimation from 'shared/components/dots-flow';
import { Evaluation } from 'redux/schemas/models/ai-role-play';
import { config } from '../../../../../../config/pendo.config.json';
import AiConversationContext from '../context';
import useViewedSubmission from '../use-viewed-submission';

type RolePlayFeedbackProps = {
  evaluation: Evaluation;
  overallSummary: string;
  whereYouDidWell?: string[];
  whereYouCanGrow?: string[];
};

const RolePlayFeedback = (props: RolePlayFeedbackProps) => {
  const {
    evaluation,
    overallSummary,
    whereYouDidWell,
    whereYouCanGrow,
  } = props;

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const {
    isRolePlayStarting,
    isHardDeadlineExpired,
    canPracticeAgain = true,
    handleStartConversation,
  } = useContext(AiConversationContext);

  const isPracticeAgainDisabled = isRolePlayStarting || isHardDeadlineExpired || !canPracticeAgain;

  const { isCurrentUserSubmission } = useViewedSubmission();

  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  const handleImageError = () => {
    // Reveal the image on error so the browser shows the alt text fallback
    setIsImageLoaded(true);
  };

  const getHeaderBackGroundColor = () => {
    switch (evaluation) {
      case Evaluation.NEED_WORK:
        return hexToRgbaString(dunes, 0.3);
      case Evaluation.GOOD_JOB:
        return hexToRgbaString(lowTide, 0.3);
      case Evaluation.EXCELLENT:
        return hexToRgbaString(seafoam, 0.3);
      default:
        return 'transparent';
    }
  };

  const getHeaderText = () => {
    switch (evaluation) {
      case Evaluation.NEED_WORK:
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.NEEDS_WORK();
      case Evaluation.GOOD_JOB:
        return t.SHARED.GOOD_JOB();
      case Evaluation.EXCELLENT:
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXCELLENT();
      default:
        return '';
    }
  };

  const renderDotsAnimationBadge = (imageSrc: string) => (
    <DotsFlowAnimation>
      <div className={`rounded-circle ${isImageLoaded ? 'bg-white' : ''}`}>
        <img
          className={`image-badge ${isImageLoaded ? '' : 'invisible'}`}
          src={prodPathReplace(imageSrc)}
          alt={getHeaderText()}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </div>
    </DotsFlowAnimation>
  );

  const renderBadge = () => {
    switch (evaluation) {
      case Evaluation.NEED_WORK:
        return renderDotsAnimationBadge('images/need-work.png');
      case Evaluation.GOOD_JOB:
        return renderDotsAnimationBadge('images/good-job.png');
      case Evaluation.EXCELLENT:
        return (
          <NvConfetti
            numberOfStars={7}
            numberOfCircles={8}
            numberOfTriangles={3}
            starColor={success}
            circleColor={lowTide}
            triangleColor={warning}
            duration={2000}
            particleSize={{ width: 12, height: 12 }}
          >
            <div className={`rounded-circle ${isImageLoaded ? 'bg-white' : ''}`}>
              <img
                className={`image-badge ${isImageLoaded ? '' : 'invisible'}`}
                src={prodPathReplace('images/excellent.png')}
                alt={getHeaderText()}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </div>
          </NvConfetti>
        );
      default:
        return null;
    }
  };

  const styles = css`
    .feedback-header {
      background-color: ${getHeaderBackGroundColor()};
    }
    .feedback-body {
      .border-bottom {
        border-color: ${gray5} !important;
      }
    }
    img.image-badge {
      width: 60px;
      height: 60px;
    }
  `;

  return (
    <div css={styles} className='d-flex flex-column w-100'>
      <div className='feedback-header py-4 px-5 d-flex flex-column align-items-center text-align-center'>
        {renderBadge()}
        <p className='page-title mt-2'>{getHeaderText()}</p>
        <p className='text-regular mb-0'>{overallSummary}</p>
      </div>
      <div className='feedback-body px-5 py-2'>
        {whereYouDidWell?.length > 0 && (
          <div className='mb-4'>
            <div className='d-flex border-bottom py-2 align-items-center'>
              <Like width={36} height={36} className='mr-2' />
              <p className='text-regular font-weight-bold mb-0'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.DID_WELL()}</p>
            </div>
            <ul className='pl-4 mb-0 pt-2'>
              {whereYouDidWell.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index} className='text-regular mb-1 ml-4'>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {whereYouCanGrow?.length > 0 && (
          <div>
            <div className='d-flex border-bottom py-2 align-items-center'>
              <InsightGrow width={36} height={36} className='mr-2' />
              <p className='text-regular font-weight-bold mb-0'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.YOU_NEED_GROW()}</p>
            </div>
            <ul className='pl-4 mb-0 pt-2'>
              {whereYouCanGrow.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index} className='text-regular mb-1 ml-4'>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {isCurrentUserSubmission && (
          <div className='py-6 d-flex justify-content-center'>
            <NvGradientBorderButton
              onClick={handleStartConversation}
              className='align-items-center py-2 px-5 practice-again-button'
              ariaLabel={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PRACTICE_AGAIN()}
              disabled={isPracticeAgainDisabled}
              dataQa={config.pendo.activities.aiRolePlay.conversationModal.practiceAgain}
            >
              {isPracticeAgainDisabled
                ? <NvIcon icon='gen-ai' size='small' className='pr-2 text-gray-4' />
                : <GradientAiIcon icon='gen-ai' size='small' className='pr-2' />}
              <span className={`${isPracticeAgainDisabled ? 'text-gray-4' : ''} text-regular bold`}>
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PRACTICE_AGAIN()}
              </span>
            </NvGradientBorderButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolePlayFeedback;
